/* eslint-disable max-len */
/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { Tooltip } from 'antd';
import { toCurrency } from 'utils/formatters';
import { COLORS } from 'components';
import store from 'store/store';
import { toMoment } from './normalizers';

const descripcionRender = (txt, record) => (record?.fecha_de_vencimiento ? (
  <>
    <Tooltip
      title={`Fecha de Vencimiento ${toMoment(record.fecha_de_vencimiento).format()}`}
    >
      {txt}
    </Tooltip>
    {' '}
  </>
) : (
  <>
    {' '}
    {txt}
    {' '}
  </>
));

const multasRender = (txt, record) => {
  const _recargos = record.recargos.filter((r) => r.estados_globales !== 5 && !r.saldado);
  const recargosMultas = _recargos.filter((i) => i.es_multa);
  const _gastos = record.gastos.filter((g) => g.estados_globales !== 5 && !g.saldado);
  const gastosMultas = _gastos.filter((i) => i.es_multa);
  const recargos = recargosMultas.map((r) => (
    <li key={r.id}>
      {`${r.descripcion}: ${toCurrency(r.importe_total)}`}
    </li>
  ));
  const gastos = gastosMultas.map((g) => (
    <li key={g.id}>
      {`${g.descripcion}: ${toCurrency(g.importe)}`}
    </li>
  ));
  const allMultas = [...recargos, ...gastos];
  return (
    <Tooltip title={allMultas?.length ? (<ul>{allMultas}</ul>) : null}>
      <span>{toCurrency(txt)}</span>
    </Tooltip>
  );
};

const descuentosMapper = (d) => (
  <li key={d.id}>
    {d.comentarios}
    :
    {' '}
    <strong>
      {toCurrency(d.importe_total)}
    </strong>
  </li>
);

const filterPorPeriodo = (d) => d.aplica_por_periodo;
const filterNoPorPeriodo = (d) => !d.aplica_por_periodo;
const some = (arr) => arr.some((e) => e.length);

/**
 * Processes discounts by applying a filter function and mapping the results to a formatted list.
 * @param {Array} items - The list of items containing discounts.
 * @param {Function} filterFn - The filter function to apply to the discounts.
 * @returns {Array} - A list of formatted discounts that match the filter criteria.
 */
const generateLabels = (items, filterFn = (d) => d.aplicado) => (items[0]?.descuentos
  ? items.map((item) => item.descuentos).flat().filter(filterFn).map(descuentosMapper)
  : items.filter(filterFn).map(descuentosMapper));

const descuentosRender = (txt, record) => {
  const {
    descuentos_aplicables,
    descuentos_especiales,
    recargos,
    recargosMulta,
    gastos,
    gastosMulta,
    actualizaciones,
  } = record;

  // Generate labels for all discount categories
  const aplicables = generateLabels(descuentos_aplicables, filterNoPorPeriodo);
  const aplicablesPorPeriodo = generateLabels(descuentos_aplicables, filterPorPeriodo);
  const especiales = generateLabels(descuentos_especiales, filterNoPorPeriodo);
  const especialesPorPeriodo = generateLabels(descuentos_especiales, filterPorPeriodo);
  const sobreRecargos = generateLabels(recargos, filterNoPorPeriodo);
  const sobreRecargosPorPeriodo = generateLabels(recargos, filterPorPeriodo);
  const sobreRecargosMulta = generateLabels(recargosMulta, filterNoPorPeriodo);
  const sobreRecargosMultaPorPeriodo = generateLabels(recargosMulta, filterPorPeriodo);
  const sobreGastos = generateLabels(gastos, filterNoPorPeriodo);
  const sobreGastosPorPeriodo = generateLabels(gastos, filterPorPeriodo);
  const sobreGastosMulta = generateLabels(gastosMulta, filterNoPorPeriodo);
  const sobreGastosMultaPorPeriodo = generateLabels(gastosMulta, filterPorPeriodo);
  const sobreActualizacion = generateLabels(actualizaciones, filterNoPorPeriodo);
  const sobreActualizacionPorPeriodo = generateLabels(actualizaciones, filterPorPeriodo);

  // Check if there are any discounts
  const hasSomeDiscount = some([
    aplicables,
    aplicablesPorPeriodo,
    especiales,
    especialesPorPeriodo,
    sobreRecargos,
    sobreRecargosPorPeriodo,
    sobreRecargosMulta,
    sobreRecargosMultaPorPeriodo,
    sobreGastos,
    sobreGastosPorPeriodo,
    sobreGastosMulta,
    sobreGastosMultaPorPeriodo,
    sobreActualizacion,
    sobreActualizacionPorPeriodo,
  ]);

  // Generate tooltip content
  const title = hasSomeDiscount ? (
    <ul>
      {some([aplicables, aplicablesPorPeriodo, especiales, especialesPorPeriodo]) && (
        <>
          <strong>Descuentos sobre cargo</strong>
          {aplicables}
          {aplicablesPorPeriodo}
          {especiales}
          {especialesPorPeriodo}
        </>
      )}
      {some([sobreRecargos, sobreRecargosPorPeriodo]) && (
        <>
          <strong>Descuentos sobre recargo</strong>
          {sobreRecargos}
          {sobreRecargosPorPeriodo}
        </>
      )}
      {some([
        sobreRecargosMulta,
        sobreRecargosMultaPorPeriodo,
        sobreGastosMulta,
        sobreGastosMultaPorPeriodo,
      ]) && (
        <>
          <strong>Descuentos sobre multas</strong>
          {sobreRecargosMulta}
          {sobreRecargosMultaPorPeriodo}
          {sobreGastosMulta}
          {sobreGastosMultaPorPeriodo}
        </>
      )}
      {some([sobreGastos, sobreGastosPorPeriodo]) && (
        <>
          <strong>Descuentos sobre gastos</strong>
          {sobreGastos}
          {sobreGastosPorPeriodo}
        </>
      )}
      {some([sobreActualizacion, sobreActualizacionPorPeriodo]) && (
        <>
          <strong>Descuentos sobre actualizaci?n</strong>
          {sobreActualizacion}
          {sobreActualizacionPorPeriodo}
        </>
      )}
    </ul>
  ) : null;

  return (
    <Tooltip title={title} onClick={(e) => e.stopPropagation()}>
      {toCurrency(txt)}
    </Tooltip>
  );
};

export const debtsColumns = [
  {
    title: 'Periodo',
    dataIndex: 'tipo_de_cargo',
    key: 'periodo_fiscal',
    width: 50,
    render: (tipo_de_cargo) => tipo_de_cargo?.periodo_fiscal?.periodo,
  },
  {
    title: 'Descripción',
    dataIndex: 'descripcion',
    key: 'descripcion',
    width: 150,
    render: descripcionRender,
  },
  {
    title: 'Adeudo Total',
    dataIndex: 'adeudo_total',
    key: 'adeudo_total',
    render: (val) => (
      <b style={{ color: COLORS.accent }}>
        {toCurrency(val || 0)}
      </b>
    ),
    align: 'right',
    width: 100,
  },
  {
    title: 'Importe Base',
    dataIndex: 'importe',
    key: 'importe',
    render: (val) => toCurrency(val),
    align: 'right',
    width: 100,
  },
  {
    title: 'Actualización',
    dataIndex: 'actualizaciones_base',
    key: 'actualizaciones_base',
    width: 70,
    align: 'right',
    render: (txt, record) => {
      const _actualizaciones = record.actualizaciones
        .filter((r) => r.estados_globales !== 5 && !r.saldado);
      const actualizaciones = _actualizaciones.length
        ? _actualizaciones.filter((i) => !i.es_multa).map((r) => (
          <li key={r.id}>
            {`${r.descripcion}: ${toCurrency(r.importe_total)}`}
          </li>
        )) : null;

      return (
        <Tooltip title={actualizaciones?.length ? (<ul>{actualizaciones}</ul>) : null}>
          <span>{toCurrency(txt)}</span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Recargos',
    dataIndex: 'recargos_base',
    key: 'recargos_base',
    width: 70,
    align: 'right',
    render: (txt, record) => {
      const _recargos = record.recargos.filter((r) => r.estados_globales !== 5 && !r.saldado);
      const recargos = _recargos.length
        ? _recargos.filter((i) => !i.es_multa).map((r) => (
          <li key={r.id}>
            {`${r.descripcion}: ${toCurrency(r.importe_total)}`}
          </li>
        )) : null;

      return (
        <Tooltip title={recargos?.length ? (<ul>{recargos}</ul>) : null}>
          <span>{toCurrency(txt)}</span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Multa',
    dataIndex: 'multas_base',
    key: 'multas_base',
    width: 70,
    align: 'right',
    render: multasRender,
  },
  {
    title: 'Gastos',
    dataIndex: 'gastos_totales',
    key: 'gastos_totales',
    width: 70,
    align: 'right',
    render: (txt, record) => {
      const _gastos = record.gastos.filter((g) => g.estados_globales !== 5 && !g.saldado);
      const gastos = _gastos.filter((i) => !i.es_multa).map((g) => (
        <li key={g.id}>
          {`${g.descripcion}: ${toCurrency(g.importe)}`}
        </li>
      ));
      return (
        <Tooltip title={gastos?.length ? (<ul>{gastos}</ul>) : null}>
          <span>{toCurrency(txt)}</span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Desc.',
    dataIndex: '_descuentos_totales_junto_accesorios',
    key: '_descuentos_totales_junto_accesorios',
    width: 70,
    align: 'right',
    render: descuentosRender,
  },
];

export const cargosSorter = (_cargos = []) => {
  const { configuracion: { ordenar_cargos_acendente = false } } = store.getState().auth.entidad;
  const sortIdentificadores = (
    { tipo_de_cargo: { identificador_periodo: a } },
    { tipo_de_cargo: { identificador_periodo: b } },
  ) => (ordenar_cargos_acendente ? a - b : b - a);

  const sortPeriodos = (a, b) => (ordenar_cargos_acendente ? a - b : b - a);
  const getAccesories = (cargo_padre) => _cargos
    .filter((e) => e.cargo_padre === cargo_padre && e.es_accesorio);

  const cargosNoAccesorios = _cargos
    .filter((c) => !c.es_accesorio)
    .sort((a, b) => (ordenar_cargos_acendente ? a.id - b.id : b.id - a.id));

  const periodos = [...new Set(cargosNoAccesorios
    .map((cargo) => cargo.tipo_de_cargo.periodo_fiscal.id))].sort(sortPeriodos);
  const entries = periodos
    .map((p) => [p, cargosNoAccesorios.filter((c) => c.tipo_de_cargo.periodo_fiscal.id === p)
      .sort(sortIdentificadores)]);
  const cargosNoAccesoriosGroupedByPeriod = Object.fromEntries(entries);
  const output = periodos.map((p) => cargosNoAccesoriosGroupedByPeriod[p]).flat()
    .reduce((a, b) => [...a, b, ...getAccesories(b.id)], []);
  return output;
};

export const periodosMap = Object.freeze({
  EVENTUAL: 1,
  DIARIO: 2,
  SEMANAL: 3,
  QUINCENAL: 4,
  MENSUAL: 5,
  BIMESTRAL: 6,
  TRIMESTRAL: 7,
  CUATRIMESTRAL: 8,
  SEMESTRAL: 9,
  ANUAL: 10,
});

const quantityOfCargosByPeriodo = Object.freeze({
  [periodosMap.DIARIO]: 365,
  [periodosMap.SEMANAL]: 52,
  [periodosMap.QUINCENAL]: 26,
  [periodosMap.MENSUAL]: 12,
  [periodosMap.BIMESTRAL]: 6,
  [periodosMap.TRIMESTRAL]: 4,
  [periodosMap.CUATRIMESTRAL]: 3,
  [periodosMap.SEMESTRAL]: 2,
  [periodosMap.ANUAL]: 1,
});

const obtenerClaveAgrupacion = (c) => {
  // este periodo hace referencia al año fiscal
  const periodoFiscal = c?.tipo_de_cargo?.periodo_fiscal?.periodo;

  // este periodo hace referencia a la frecuencia con la que se aplica el cargo
  const { periodo } = c.tipo_de_cargo;
  const sistema = c.tipo_de_cargo.sistemas?.id || c.tipo_de_cargo.sistemas;
  return `${periodoFiscal}__${sistema}__${periodo}`;
};

const agruparCargosPorPeriodos = (cargos) => {
  const agrupados = {};

  cargos.forEach((cargo) => {
    if (cargo.es_accesorio) return;

    const clave = obtenerClaveAgrupacion(cargo);
    if (!agrupados[clave]) agrupados[clave] = [];
    agrupados[clave].push(cargo.id);
  });

  return agrupados;
};

const actualizarDescuentoPorPeriodo = (cargos) => {
  const cargosAgrupadosPorPeriodos = agruparCargosPorPeriodos(cargos);

  return cargos.map((c) => {
    const key = obtenerClaveAgrupacion(c);
    const quantity = quantityOfCargosByPeriodo[c?.tipo_de_cargo?.periodo];
    const cargosPeriodo = cargosAgrupadosPorPeriodos[key] || [];

    const aplicaDescuentoPorPeriodo = cargosPeriodo.length === quantity;

    return {
      ...c,
      aplicaDescuentoPorPeriodo,
      cargosPeriodo,
    };
  });
};

const filterMultas = (d) => d.es_multa;
const filterNoMultas = (d) => !d.es_multa;
const reduceImporteTotal = (arr) => arr.reduce((accum, curr) => accum + curr.importe_total, 0);

const filtrarDescuentosActivos = (descuentos) => descuentos.filter((d) => d.estados_globales !== 5 && !d.cancelado && !d.saldado);

const filtrarDescuentosEspeciales = (descuentos) => descuentos.filter((d) => d.estados_globales === 4);

const obtenerDescuentosAplicables = (descuentos, aplicaDescuentoPorPeriodo) => {
  const descuentosNoPeriodo = descuentos.filter(filterNoPorPeriodo);

  if (!aplicaDescuentoPorPeriodo) {
    return descuentosNoPeriodo;
  }

  const descuentosPorPeriodo = descuentos.filter(filterPorPeriodo);
  return [...descuentosNoPeriodo, ...descuentosPorPeriodo];
};

const aplicarDescuentoMayor = (descuentosAplicados) => {
  if (!descuentosAplicados.length) {
    return descuentosAplicados;
  }

  const [descuentoMayor] = descuentosAplicados.sort((a, b) => b.importe_total - a.importe_total);
  return [descuentoMayor];
};

const makeDescuentosNormalizer = (
  solo_aplica_el_descuento_mayor,
  aplicaDescuentoPorPeriodo,
  keyDescuentos = 'descuentos',
) => (item) => {
  // Obtener y filtrar descuentos iniciales
  const descuentosArr = item[keyDescuentos] ?? [];
  let descuentos = filtrarDescuentosActivos([...descuentosArr]);

  // Aplicar filtro adicional para descuentos especiales
  if (keyDescuentos === 'descuentos_especiales') {
    descuentos = filtrarDescuentosEspeciales(descuentos);
  }

  // Obtener descuentos aplicables según el período
  let descuentosAplicados = obtenerDescuentosAplicables(descuentos, aplicaDescuentoPorPeriodo);

  // Aplicar lógica de descuento mayor si es necesario
  if (solo_aplica_el_descuento_mayor) {
    descuentosAplicados = aplicarDescuentoMayor(descuentosAplicados);
  }

  // Marcar descuentos como aplicados
  const idsDescuentosAplicados = descuentosAplicados.map((d) => d.id);
  const descuentosFinales = descuentos.map((d) => ({
    ...d,
    aplicado: idsDescuentosAplicados.includes(d.id),
  }));

  return { ...item, [keyDescuentos]: descuentosFinales };
};

const filterAplicado = (d) => d.aplicado;
const filterDescuentosAplicados = (arr) => arr.map((e) => e.descuentos.filter(filterAplicado)).flat();

const debtsFormatter = (cargos = []) => {
  const _cargos = actualizarDescuentoPorPeriodo(cargos)
    .map((cargo) => {
      const {
        aplicaDescuentoPorPeriodo,
        tipo_de_cargo: { solo_aplica_el_descuento_mayor },
      } = cargo;

      const defDescuentosNormalizer = makeDescuentosNormalizer(solo_aplica_el_descuento_mayor, aplicaDescuentoPorPeriodo);
      const descuentosAplicablesNormalizer = makeDescuentosNormalizer(solo_aplica_el_descuento_mayor, aplicaDescuentoPorPeriodo, 'descuentos_aplicables');
      const descuentosEspecialesNormalizer = makeDescuentosNormalizer(solo_aplica_el_descuento_mayor, aplicaDescuentoPorPeriodo, 'descuentos_especiales');

      const [{ descuentos_aplicables }] = [cargo].map(descuentosAplicablesNormalizer);
      const [{ descuentos_especiales }] = [cargo].map(descuentosEspecialesNormalizer);
      const recargos = cargo.recargos?.filter(filterNoMultas).map(defDescuentosNormalizer) || [];
      const gastos = cargo.gastos?.filter(filterNoMultas).map(defDescuentosNormalizer) || [];
      const actualizaciones = cargo.actualizaciones?.map(defDescuentosNormalizer) || [];
      const recargosMulta = cargo.recargos?.filter(filterMultas).map(defDescuentosNormalizer) || [];
      const gastosMulta = cargo.gastos?.filter(filterNoMultas).map(defDescuentosNormalizer) || [];

      const recargos_base = reduceImporteTotal(recargos);
      const gastos_totales = reduceImporteTotal(gastos);
      const actualizaciones_base = reduceImporteTotal(actualizaciones);
      const multas_base = reduceImporteTotal(recargosMulta.concat(gastosMulta));

      const descuentos_aplicables_total = reduceImporteTotal(descuentos_aplicables.filter(filterAplicado));
      const descuentos_especiales_total = reduceImporteTotal(descuentos_especiales.filter(filterAplicado));

      const descuentos_sobre_cargo = descuentos_aplicables_total + descuentos_especiales_total;
      const descuentos_de_recargos = reduceImporteTotal(filterDescuentosAplicados(recargos));
      const descuentos_actualizacion = reduceImporteTotal(filterDescuentosAplicados(actualizaciones));
      const descuentos_gastos_totales = reduceImporteTotal(filterDescuentosAplicados(gastos));
      const descuentos_gastos_totales_multas = reduceImporteTotal(filterDescuentosAplicados(recargosMulta));
      const descuentos_de_recargos_multas = reduceImporteTotal(filterDescuentosAplicados(gastosMulta));

      const multa_gastos = reduceImporteTotal(gastosMulta);
      const recargo_total_multas = reduceImporteTotal(recargosMulta);

      const multas_totales = (multa_gastos - descuentos_gastos_totales_multas)
        + (recargo_total_multas - descuentos_de_recargos_multas);

      const adeudo_total = ((cargo.importe - descuentos_sobre_cargo
        + (recargos_base - descuentos_de_recargos)
        + (actualizaciones_base - descuentos_actualizacion)
        + (gastos_totales - descuentos_gastos_totales)
        + multas_totales) * 100) / 100;

      const _descuentos_totales_junto_accesorios = descuentos_sobre_cargo + descuentos_de_recargos
        + descuentos_actualizacion + descuentos_gastos_totales + descuentos_gastos_totales_multas
        + descuentos_de_recargos_multas;

      return {
        ...cargo,
        // para descuentosRender
        descuentos_aplicables,
        descuentos_especiales,
        recargos,
        gastos,
        actualizaciones,
        recargosMulta,
        gastosMulta,
        // para TableAdeudo
        adeudo_total,
        actualizaciones_base,
        recargos_base,
        multas_base,
        gastos_totales,
        _descuentos_totales_junto_accesorios,
        // para GPM
        subtotal_gpm: adeudo_total - recargos_base,
      };
    });
  return _cargos.reverse();
};

/**
 * Extrae los IDs de los descuentos aplicados de una lista de cargos.
 * La función procesa descuentos de diferentes fuentes:
 * - Descuentos aplicables
 * - Descuentos especiales
 * - Descuentos en recargos
 * - Descuentos en recargos de multas
 * - Descuentos en actualizaciones
 * - Descuentos en gastos
 * - Descuentos en gastos de multas
 *
 * @param {Array} cargos - Lista de cargos a procesar
 * @param {Number} cargos.id - ID del cargo
 * @param {Array} cargos.descuentos_aplicables - Descuentos aplicables
 * @param {Array} cargos.descuentos_especiales - Descuentos especiales
 * @param {Array} cargos.recargos - Recargos
 * @param {Array} cargos.recargosMulta - Recargos de multas
 * @param {Array} cargos.actualizaciones - Actualizaciones
 * @param {Array} cargos.gastos - Gastos
 * @returns {Array<Object>} Array de IDs de descuentos aplicados
 * @throws {Error} Si hay un error al procesar los descuentos
 */
export const extractDescuentosIds = (cargos = []) => {
  try {
    if (!Array.isArray(cargos)) {
      throw new Error('El parámetro cargos debe ser un array');
    }

    const extraerDescuentosDeCargo = (cargo) => {
      const descuentos = [
        ...(cargo.descuentos_aplicables || []),
        ...(cargo.descuentos_especiales || []),
        ...(cargo.recargos || []).map((r) => r.descuentos || []),
        ...(cargo.recargosMulta || []).map((r) => r.descuentos || []),
        ...(cargo.actualizaciones || []).map((a) => a.descuentos || []),
        ...(cargo.gastos || []).map((g) => g.descuentos || []),
        ...(cargo.gastosMulta || []).map((g) => g.descuentos || []),
      ];

      return descuentos.flat(2)
        .filter((d) => d && d.aplicado)
        .map((d) => d.id);
    };

    return cargos
      .map(extraerDescuentosDeCargo)
      .flat()
      .filter((id) => id !== undefined && id !== null);
  } catch (error) {
    throw new Error(`Error al procesar descuentos: ${error.message}`);
  }
};

export default debtsFormatter;
